import { createBrowserRouter, RouterProvider, Navigate, Outlet, useRouteError } from 'react-router-dom';
import { AuthProvider } from './lib/auth-context.js';
import { OrgProvider } from './lib/org-context.js';
import { ToastProvider } from './components/Toast.js';

// Layouts
import { PublicLayout } from './layouts/PublicLayout.js';
import { CustomerLayout } from './layouts/CustomerLayout.js';
import { AdminLayout } from './layouts/AdminLayout.js';

// Guards
import { RequireAuth } from './guards/RequireAuth.js';
import { RequireSuperAdmin } from './guards/RequireSuperAdmin.js';
import { RedirectSuperAdmin } from './guards/RedirectSuperAdmin.js';

// Pages
import { LoginPage } from './pages/LoginPage.js';
import { SignupPage } from './pages/SignupPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LeadFeedPage } from './pages/LeadFeedPage.js';
import { IntegrationsPage } from './pages/IntegrationsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { AdminOrgsPage } from './pages/AdminOrgsPage.js';
import { SystemPage } from './pages/SystemPage.js';
import { AdminMetricsPage } from './pages/AdminMetricsPage.js';
import { AdminSettingsPage } from './pages/AdminSettingsPage.js';
import { DripPage } from './pages/DripPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { InviteAcceptPage } from './pages/InviteAcceptPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';
import { ProgramsPage } from './pages/ProgramsPage.js';
import { ProfileSection as ProfilePage } from './pages/ProfilePage.js';
import { AdminTrialsPage } from './pages/AdminTrialsPage.js';
import { MyWebsitePage } from './pages/MyWebsitePage.js';

/**
 * Root layout that provides context providers to all routes.
 * This is necessary because createBrowserRouter renders outside
 * the normal React tree, so contexts must be provided via a root route.
 */
function RootProviders() {
  return (
    <ToastProvider>
      <AuthProvider>
        <OrgProvider>
          <Outlet />
        </OrgProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-6xl font-bold text-gray-200 mb-4">404</p>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Page not found</h2>
        <p className="text-gray-500 text-sm mb-4">The page you're looking for doesn't exist.</p>
        <a href="/app" className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 inline-block">Go to Dashboard</a>
      </div>
    </div>
  );
}

function ErrorFallback() {
  const error = useRouteError();
  console.error('Route error:', error);
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h2>
        <p className="text-gray-500 text-sm mb-4">An unexpected error occurred.</p>
        <button onClick={() => window.location.href = '/'} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">
          Go to Home
        </button>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RootProviders />,
    errorElement: <ErrorFallback />,
    children: [
      // Full-page auth routes (own layouts with gradient backgrounds)
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },

      // Full-page invite accept (own gradient layout)
      { path: '/invite/:token', element: <InviteAcceptPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },

      // Customer routes (auth required, super_admin redirected to admin panel)
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RedirectSuperAdmin />,
            children: [{
            path: '/app',
            element: <CustomerLayout />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'leads', element: <LeadFeedPage /> },
              { path: 'integrations', element: <IntegrationsPage /> },
              { path: 'drip', element: <DripPage /> },
              { path: 'team', element: <TeamPage /> },
              { path: 'programs', element: <ProgramsPage /> },
              { path: 'website', element: <MyWebsitePage /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'settings', element: <SettingsPage /> },
            ],
          }],
          },
        ],
      },

      // Admin routes (super_admin required)
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireSuperAdmin />,
            children: [
              {
                path: '/admin',
                element: <AdminLayout />,
                children: [
                  { index: true, element: <Navigate to="/admin/orgs" replace /> },
                  { path: 'orgs', element: <AdminOrgsPage /> },
                  { path: 'metrics', element: <AdminMetricsPage /> },
                  { path: 'settings', element: <AdminSettingsPage /> },
                  { path: 'system', element: <SystemPage /> },
                  { path: 'trials', element: <AdminTrialsPage /> },
                ],
              },
            ],
          },
        ],
      },

      // Root redirect
      { path: '/', element: <Navigate to="/app" replace /> },

      // Catch-all
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
